/**
 * authorizeCardHtml.ts — the PURE html for every held-action card, in one place.
 *
 * WHY IT IS ITS OWN MODULE (P4, 2026-09-16). The card is DATA; its html is a
 * projection (contract C2). A governance card posted into an approver's own
 * thread is rebuilt from its persisted payload at every hydrate, and that
 * rebuild runs INSIDE the chat reducer (connectTileRehydrate → useGlobalChat),
 * so the builder it calls may not drag `firebase/auth` into the reducer's
 * import graph — `src/services/prest0nSession/client.ts` imports it and touches
 * global fetch at import time (the load-time trap CLAUDE.md names). Everything
 * here imports the pure card kit and TYPES ONLY, so the markup has exactly one
 * owner and the reducer can call it.
 *
 * TWO KINDS, ONE FAMILY:
 *   - a held SEND (2026-08-18): recipient, subject, the full draft, send /
 *     don't-send. Byte-identical to what shipped — these bodies were moved
 *     here unchanged.
 *   - a held TEAM OR ROLE CHANGE (P4): the change in one line, the facts
 *     beneath it, and the decision. Three views, because three different
 *     people can be looking at the same hold:
 *       self      — the asker may make the change; the ladder asks them to
 *                   confirm once (Miles, 2026-09-15: card for roles, first
 *                   invite only).
 *       approver  — the asker may NOT; this is a colleague who can, reading it
 *                   in a thread of their own. Their tap makes the change under
 *                   THEIR authority.
 *       waiting   — the asker's own card while an approver decides. It offers
 *                   only "withdraw": the asker can never approve their own ask,
 *                   and the server refuses it even if this html lied.
 *
 * D9: every string here is state, refusal or failure copy — the facts of what
 * was asked and what happened, never content.
 */

import type {
  PendingActionOutcome,
  PendingActionPreview,
} from '../../prest0nSession/client';
import {
  P0AC,
  escapeHtml,
  renderCardShell,
  renderStateIcon,
  renderPillButton,
  renderMetaRow,
  renderDetailBlock,
  renderBodyBlock,
  renderFooterRow,
} from './actionCardKit';
import { failureNeedsExplainTurn } from './sendOutcomePolicy';

/** Button id prefixes for the click handler (per-action suffix keeps the
 * double-click guard scoped to one card). */
export const AUTHORIZE_ACTION_BUTTON_IDS = {
  approve: 'authorize-action-approve',
  cancel: 'authorize-action-cancel',
  /** On a receipt whose failure left the draft armed — see `retryable`. */
  retry: 'authorize-action-retry',
} as const;

// Visuals come from actionCardKit — one design system across every send
// surface (owner decision 2026-08-27). CONFIRMATION IS PREST0 GOLD, NOT
// GREEN (owner directive 2026-08-25, ask 6): P0AC.OK_INK is the house gold.
const SUBTLE = P0AC.SUBTLE;
const OK_INK = P0AC.OK_INK;
const WARN_INK = P0AC.WARN_INK;

/** The authorize card: one-row sibling of the batch card — same kit, same
 *  family (owner decision 2026-08-27: one design system across all send
 *  surfaces). Every id, data attribute, and copy string is preserved. */
function buildEmailAuthorizeCardHtml(actionId: string, preview: PendingActionPreview): string {
  const id = escapeHtml(actionId);
  const to = escapeHtml(preview.to || '');
  const cc = escapeHtml(preview.cc || '');
  const subject = escapeHtml(preview.subject || '');
  // ONE TILE (owner directive 2026-08-29): the card IS the whole message —
  // addressing, the FULL draft body as the card's own flowing prose (house
  // typography, full width — never the old boxed preview window, which
  // stays gone), and the decision buttons. The model emits empty content;
  // nothing is restated in the reply. `bodyPreview` carries the full body
  // since the same change (20k server cap, `bodyTruncated` marks overflow).
  // WHICH MAILBOX (owner directive 2026-08-25): before anything goes out,
  // the card names the account it goes out FROM.
  const from = escapeHtml(preview.from || '');
  const approveId = `${AUTHORIZE_ACTION_BUTTON_IDS.approve}-${actionId}`;
  const cancelId = `${AUTHORIZE_ACTION_BUTTON_IDS.cancel}-${actionId}`;
  const dataAttrs = `data-action-id="${id}" data-to="${to}" data-subject="${subject}"`;
  return renderCardShell({
    maxWidth: 560,
    title: 'ready to send — your call',
    bodyHtml: `<div class="p0ac-row-meta" style="margin-top:10px;font-size:0.84rem;">
    ${from ? `${renderMetaRow('from:', from)}\n    ` : ''}${renderMetaRow('to:', to)}${cc ? `\n    ${renderMetaRow('cc:', cc)}` : ''}
    ${renderMetaRow('subject:', subject)}
  </div>
  ${renderBodyBlock(preview.bodyPreview || '', preview.bodyTruncated)}
  <div class="p0ac-note">nothing goes out until you tap send it — and you will get a receipt here either way.</div>
  <div class="p0ac-actions">
    ${renderPillButton({ id: approveId, label: 'send it', variant: 'primary', size: 'md', dataAttrs })}
    ${renderPillButton({ id: cancelId, label: "don't send", variant: 'ghost', size: 'md', dataAttrs })}
  </div>`,
  });
}

/** Which visual state a receipt renders in. */
type ReceiptTone = 'sent' | 'not-sent' | 'unconfirmed';

function receiptTone(outcome: PendingActionOutcome): ReceiptTone {
  if (outcome.delivered) return 'sent';
  if (outcome.kind === 'unconfirmed' || outcome.kind === 'unreachable') return 'unconfirmed';
  return 'not-sent';
}

/** Short local clock label for the receipt line. */
function receiptClock(decidedAt?: string): string {
  const when = decidedAt ? new Date(decidedAt) : new Date();
  if (Number.isNaN(when.getTime())) return '';
  try {
    return when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * The receipt card. A record, not a control — with ONE exception.
 *
 * It stays buttonless for every DECIDED outcome (sent, cancelled, refused for
 * a reason the same payload cannot survive), because a decided card that
 * still looks clickable is what the receipt was invented to stop.
 *
 * The exception is a failure the server has certified `retryable`: nothing
 * left the building and the SAME held draft is still armed. That is not a
 * decided send, it is an interrupted one, and the honest control for it is a
 * retry. Without it the 2026-08-24 receipt promised "once fixed, the same
 * send will work — no new draft needed" and then offered the user no way to
 * do that, which is how Prest0n ended up promising it again in prose.
 */
function buildEmailSendReceiptCardHtml(outcome: PendingActionOutcome): string {
  const tone = receiptTone(outcome);
  const ink = tone === 'sent' ? OK_INK : WARN_INK;
  const title =
    tone === 'sent'
      // "auto sent" when the standing teach authorized it (owner directive
      // 2026-08-25, ask 5) — the mode is never invisible on the record.
      ? outcome.autoApproved
        ? 'auto sent — confirmed by your email provider'
        : 'sent — confirmed by your email provider'
      : outcome.kind === 'cancelled'
        ? 'not sent — you cancelled it'
        : outcome.kind === 'superseded'
          // A newer draft replaced this card — the user cancelled NOTHING
          // (owner feedback 2026-08-25, ask 2).
          ? 'not sent — a newer draft replaced this one'
        : outcome.kind === 'expired'
          ? 'not sent — this card expired before it was sent'
          : outcome.kind === 'not-allowed'
            ? 'not sent — your role cannot send from this workspace'
            : outcome.kind === 'unreachable'
              ? 'not confirmed — the click never reached us'
              // These two used to explain themselves through a raw server
              // string pasted into "why:". That string is ours, not the email
              // provider's, so it no longer reaches the card — which means the
              // TITLE has to carry the explanation instead of leaving a bare
              // "not sent" the user cannot act on.
              : outcome.kind === 'already-decided'
                ? 'not sent — this card was already answered'
              : outcome.kind === 'not-found'
                ? 'not sent — this card is no longer available'
              : outcome.kind === 'internal-error'
                // Name the owner of the problem in the title itself. The
                // 2026-08-24 receipt said a bare "not sent" over a raw
                // 'COMPOSIO_API_KEY environment variable not set', which read
                // as "your email is broken". It was ours.
                ? 'not sent — a problem on our side, not yours'
                : tone === 'unconfirmed'
                  ? 'not confirmed — we cannot say it went out'
                  : 'not sent';
  const clock = receiptClock(outcome.decidedAt);
  const to = escapeHtml(outcome.to || '');
  const subject = escapeHtml(outcome.subject || '');
  const reason = escapeHtml(outcome.reason || '');
  const remediation = escapeHtml(outcome.remediation || '');
  // The FROM line replaces the provider id (owner directive 2026-08-25,
  // ask 4): "provider ID doesn't mean anything to the user". The id still
  // rides the structured payload for the record; it just never renders.
  const from = escapeHtml(outcome.from || '');

  const rows: string[] = [];
  if (from) rows.push(renderMetaRow('from:', from));
  if (to) rows.push(renderMetaRow('to:', to));
  if (subject) rows.push(renderMetaRow('subject:', subject));
  if (clock) {
    rows.push(renderMetaRow(tone === 'sent' ? 'sent at:' : 'at:', escapeHtml(clock)));
  }
  if (tone === 'sent') rows.push(`<div style="color:${SUBTLE};">it is in your Sent folder now.</div>`);

  const detail: string[] = [];
  if (tone !== 'sent' && reason) detail.push(renderMetaRow('why:', reason));
  if (tone !== 'sent' && remediation) {
    detail.push(`<div style="margin-top:6px;">${renderMetaRow('next:', remediation)}</div>`);
  }

  const actionId = escapeHtml(outcome.actionId || '');
  const retryId = `${AUTHORIZE_ACTION_BUTTON_IDS.retry}-${outcome.actionId || ''}`;
  // The retry renders only when this receipt will be the LAST word in the
  // thread. workflowCtaLifecycle is latest-actionable-only: any newer message
  // greys every button on the card above it, so a retry printed under an
  // outcome that also spawns an explain turn would arrive already dead. A
  // button that greys out a second after it appears is worse than no button.
  // (The server-side re-arm is NOT limited this way — a hold stays armed for
  // every re-armable kind, so "send it again" in words still works.)
  const retryStaysLive = !failureNeedsExplainTurn(outcome.kind);
  const retry =
    outcome.retryable && actionId && retryStaysLive
      ? `\n  ${renderFooterRow({
          buttonHtml: renderPillButton({
            id: retryId,
            label: 'try again',
            variant: 'primary',
            size: 'md',
            dataAttrs: `data-action-id="${actionId}" data-to="${to}" data-subject="${subject}"`,
          }),
          helperText: 'same email, nothing re-written.',
        })}`
      : '';

  // Tone lives in the title ink + state icon. why:/next: are plain LINES,
  // not a boxed window (owner directive 2026-08-29 — no preview/detail
  // windows inside action tiles): they read as part of the receipt instead
  // of an inset console pane. Gold for confirmed, never green.
  const titleIcon = tone === 'sent'
    ? renderStateIcon('check')
    : tone === 'unconfirmed'
      ? renderStateIcon('clock')
      : renderStateIcon('alert');
  return renderCardShell({
    maxWidth: 560,
    title,
    titleInk: ink,
    titleIconSvg: titleIcon,
    bodyHtml: `<div class="p0ac-row-meta" style="margin-top:10px;font-size:0.84rem;">
    ${rows.join('\n    ')}
  </div>${detail.length > 0 ? `\n  ${renderDetailBlock(detail)}` : ''}${retry}`,
  });
}


// ============================================================================
// THE GOVERNANCE CARD (P4, 2026-09-16) — a held team or role change
// ============================================================================

/** Which of the three readers is looking at this hold. */
export type GovernanceCardView = 'self' | 'approver' | 'waiting';

/** PURE. The view a card renders in, from the hold's own facts. */
export function governanceCardView(
  preview: PendingActionPreview,
  explicit?: GovernanceCardView | null,
): GovernanceCardView {
  if (explicit === 'self' || explicit === 'approver' || explicit === 'waiting') return explicit;
  return preview.awaitingApproval === true ? 'waiting' : 'self';
}

/** PURE. Is this hold a team/role change rather than an email? */
export function isGovernancePreview(preview: PendingActionPreview | null | undefined): boolean {
  return !!preview && preview.kind === 'rbac';
}

const GOVERNANCE_TITLES: Record<GovernanceCardView, string> = {
  self: 'confirm this change — your call',
  approver: 'a teammate needs your approval',
  waiting: 'waiting on approval',
};

function governanceSummaryOf(preview: PendingActionPreview): string {
  return String(preview.summary || preview.subject || 'a team or role change');
}

function buildGovernanceCardHtml(
  actionId: string,
  preview: PendingActionPreview,
  view: GovernanceCardView,
): string {
  const id = escapeHtml(actionId);
  const summary = escapeHtml(governanceSummaryOf(preview));
  const lines = Array.isArray(preview.lines) ? preview.lines : [];
  const askedBy = escapeHtml(String(preview.askedBy || ''));
  const approvers = Array.isArray(preview.approvers) ? preview.approvers.filter(Boolean) : [];
  // `data-kind` is what tells the click handler this is not a send, so its
  // button states read "approving…" instead of "sending…".
  const dataAttrs = `data-action-id="${id}" data-kind="rbac" data-subject="${summary}"`;
  const approveId = `${AUTHORIZE_ACTION_BUTTON_IDS.approve}-${actionId}`;
  const cancelId = `${AUTHORIZE_ACTION_BUTTON_IDS.cancel}-${actionId}`;

  const rows: string[] = [];
  if (view === 'approver' && askedBy) rows.push(renderMetaRow('asked by:', askedBy));
  rows.push(renderMetaRow('change:', summary));
  for (const line of lines.slice(0, 8)) {
    const text = String(line || '').trim();
    if (!text) continue;
    const at = text.indexOf(':');
    if (at > 0 && at < 28) {
      rows.push(renderMetaRow(`${text.slice(0, at)}:`, escapeHtml(text.slice(at + 1).trim())));
    } else {
      rows.push(`<div>${escapeHtml(text)}</div>`);
    }
  }

  const note = view === 'waiting'
    ? approvers.length > 0
      ? `asked ${escapeHtml(approvers.join(' and '))}. nothing changes until one of them approves — you'll hear back here.`
      : "nothing changes until someone who can approve it does — you'll hear back here."
    : view === 'approver'
      ? 'nothing changes until you decide, and the change is made under your access — not theirs.'
      : 'nothing changes until you tap — and you will get a receipt here either way.';

  const actions = view === 'waiting'
    ? renderPillButton({ id: cancelId, label: 'withdraw', variant: 'ghost', size: 'md', dataAttrs })
    : `${renderPillButton({
        id: approveId,
        label: view === 'approver' ? 'approve' : 'make the change',
        variant: 'primary',
        size: 'md',
        dataAttrs,
      })}
    ${renderPillButton({
      id: cancelId,
      label: view === 'approver' ? 'decline' : "don't",
      variant: 'ghost',
      size: 'md',
      dataAttrs,
    })}`;

  return renderCardShell({
    maxWidth: 560,
    title: GOVERNANCE_TITLES[view],
    bodyHtml: `<div class="p0ac-row-meta" style="margin-top:10px;font-size:0.84rem;">
    ${rows.join('\n    ')}
  </div>
  <div class="p0ac-note">${note}</div>
  <div class="p0ac-actions">
    ${actions}
  </div>`,
  });
}

/**
 * The offer card for ANY held action. A send keeps its exact previous html;
 * a team/role change renders the governance card in the right view.
 */
export function buildAuthorizeCardHtml(
  actionId: string,
  preview: PendingActionPreview,
  opts?: { view?: GovernanceCardView | null },
): string {
  if (isGovernancePreview(preview)) {
    return buildGovernanceCardHtml(actionId, preview, governanceCardView(preview, opts?.view));
  }
  return buildEmailAuthorizeCardHtml(actionId, preview);
}

/** PURE. The three governance outcome kinds, as the receipt reads them. */
export function isGovernanceOutcomeKind(kind: string | null | undefined): boolean {
  const k = String(kind || '');
  return k === 'rbac-done' || k === 'rbac-refused' || k === 'rbac-failed';
}

function buildGovernanceReceiptCardHtml(outcome: PendingActionOutcome): string {
  const done = outcome.kind === 'rbac-done';
  const declined = outcome.decision === 'cancel';
  const ink = done ? OK_INK : WARN_INK;
  const title = done
    ? 'done — the change was made'
    : declined
      ? 'not changed — declined'
      : outcome.kind === 'rbac-refused'
        ? 'not changed — the platform refused it'
        : outcome.kind === 'rbac-failed'
          ? 'not changed — a problem on our side, not yours'
          : outcome.kind === 'permission-denied'
            ? 'not changed — this needs access you do not have'
            : outcome.kind === 'expired'
              ? 'not changed — this card expired before it was decided'
              : outcome.kind === 'already-decided'
                ? 'not changed here — this card was already answered'
                : outcome.kind === 'superseded'
                  ? 'not changed — a newer request replaced this one'
                  : outcome.kind === 'unreachable'
                    ? 'not confirmed — the tap never reached us'
                    : 'not changed';
  const summary = escapeHtml(String(outcome.subject || 'the change'));
  const clock = receiptClock(outcome.decidedAt);
  const rows: string[] = [renderMetaRow('change:', summary)];
  if (clock) rows.push(renderMetaRow(done ? 'made at:' : 'at:', escapeHtml(clock)));

  const detail: string[] = [];
  // "why:" is the platform's OWN code-authored verdict for a refusal. Our
  // internal failures withhold it server-side, exactly as a send does.
  if (!done && outcome.reason) detail.push(renderMetaRow('why:', escapeHtml(String(outcome.reason))));
  if (!done && outcome.remediation) {
    detail.push(`<div style="margin-top:6px;">${renderMetaRow('next:', escapeHtml(String(outcome.remediation)))}</div>`);
  }

  return renderCardShell({
    maxWidth: 560,
    title,
    titleInk: ink,
    titleIconSvg: done ? renderStateIcon('check') : renderStateIcon('alert'),
    bodyHtml: `<div class="p0ac-row-meta" style="margin-top:10px;font-size:0.84rem;">
    ${rows.join('\n    ')}
  </div>${detail.length > 0 ? `\n  ${renderDetailBlock(detail)}` : ''}`,
  });
}

/**
 * The receipt for ANY decided held action. A send keeps its exact previous
 * html; a decided team/role change gets the governance receipt — buttonless
 * always, because a governance hold never re-arms (`retryable:false`): the
 * change either happened or the request is over.
 */
export function buildSendReceiptCardHtml(outcome: PendingActionOutcome): string {
  if (isGovernanceOutcomeKind(outcome.kind)) {
    return buildGovernanceReceiptCardHtml(outcome);
  }
  return buildEmailSendReceiptCardHtml(outcome);
}
